import { UserCompanyRole } from '@@types/api.types';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { cn } from '@lib/utils';
import type { FC } from 'react';

const REMOVE_VALUE = 'REMOVE';

const ROLE_OPTIONS: Array<{ label: string; value: UserCompanyRole }> = [
  { label: 'Admin', value: UserCompanyRole.Admin },
  { label: 'Collaborator', value: UserCompanyRole.Collaborator },
];

type OrganizationRoleSelectProps = {
  className?: string;
  disabled?: boolean;
  // When provided, the dropdown shows a red "Remove" option that removes the member from the org.
  onRemove?: () => void;
  onRoleChange: (role: UserCompanyRole) => void;
  role?: UserCompanyRole | null;
};

// AZ-451: generic company-role selector for non-Intelligence orgs, mirroring `IntelligencePersonaSelect`
// — picking an option updates the role; the red "Remove" option removes the member.
export const OrganizationRoleSelect: FC<OrganizationRoleSelectProps> = (props) => {
  const { className, disabled, onRemove, onRoleChange, role } = props;

  const handleValueChange = (value: string): void => {
    if (value === REMOVE_VALUE) {
      onRemove?.();
      return;
    }
    const nextRole = ROLE_OPTIONS.find((option) => option.value === value)?.value;
    if (nextRole) {
      onRoleChange(nextRole);
    }
  };

  return (
    <Select
      disabled={disabled}
      onValueChange={handleValueChange}
      value={role ?? undefined}
    >
      <SelectTrigger className={cn('h-10 border border-solid border-gray-200 bg-gray-100 p-3', className)}>
        <SelectValue placeholder="Select role" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {onRemove && (
            <SelectItem
              className="text-red-500 focus:text-red-500"
              value={REMOVE_VALUE}
            >
              Remove
            </SelectItem>
          )}
          {ROLE_OPTIONS.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
